import { useState } from "react";
import { NavLink } from "react-router-dom";
import { T } from "./theme";
import {
  LayoutDashboard, MessageSquare, BarChart2, ClipboardList,
  Sparkles, Settings, ChevronLeft, ChevronRight, Lightbulb,
  TrendingUp, Building2, ShieldCheck, Network,
} from "lucide-react";

const GROUPS = [
  {
    label: "ANALYTICS",
    items: [
      { to: "/",             icon: LayoutDashboard, label: "Dashboard"    },
      { to: "/depoimentos",  icon: MessageSquare,   label: "Depoimentos"  },
      { to: "/comparativo",  icon: BarChart2,       label: "Comparativo"  },
      { to: "/benchmarking", icon: Building2,       label: "Benchmarking" },
    ],
  },
  {
    label: "INTELIGÊNCIA",
    items: [
      { to: "/rankings",  icon: TrendingUp,    label: "Rankings"     },
      { to: "/tips",      icon: Lightbulb,     label: "Tips CXO"     },
      { to: "/pdsa",      icon: ClipboardList, label: "PDSA"         },
      { to: "/qualidade", icon: Sparkles,      label: "Qualidade IA" },
    ],
  },
  {
    label: "GESTÃO",
    items: [
      { to: "/admin", icon: ShieldCheck, label: "Admin", role: ["admin"]        },
      { to: "/crm",   icon: Network,     label: "CRM",   role: ["admin","cxo"]  },
    ],
  },
  {
    label: "SISTEMA",
    items: [
      { to: "/configuracoes", icon: Settings, label: "Configurações" },
    ],
  },
];

export default function Sidebar({ session, onLogout }) {
  const [collapsed, setCollapsed] = useState(false);

  const role = session?.usuario?.role || "viewer";

  const canSeeItem = (item) => {
    if (!item.role) return true;
    return item.role.includes(role);
  };

  return (
    <div style={{
      width: collapsed ? 80 : 260,
      background: T.bgSidebar,
      borderRight: `1px solid ${T.border}`,
      display: "flex", flexDirection: "column",
      transition: "width 0.3s cubic-bezier(0.16, 1, 0.3, 1)", flexShrink: 0,
      height: "100vh", overflow: "hidden",
      boxShadow: "1px 0 0 #E5E7EB",
      zIndex: 60,
    }}>
      {/* Logo */}
      <div style={{
        padding: collapsed ? "20px 0" : "24px 24px",
        display: "flex", alignItems: "center",
        justifyContent: collapsed ? "center" : "space-between",
        minHeight: 80,
      }}>
        {!collapsed && (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 10,
              background: T.gradientAccent,
              display: "flex", alignItems: "center", justifyContent: "center",
              flexShrink: 0,
              boxShadow: `0 4px 12px ${T.primaryGlow}`,
            }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
                <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
              </svg>
            </div>
            <span style={{
              fontFamily: T.fontTitle, fontWeight: 800, fontSize: 18,
              color: T.textHi, letterSpacing: "-0.03em",
            }}>TrueNPS</span>
          </div>
        )}
        {collapsed && (
          <div style={{
            width: 32, height: 32, borderRadius: 10,
            background: T.gradientAccent,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 4px 12px ${T.primaryGlow}`,
          }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
            </svg>
          </div>
        )}
        {!collapsed && (
          <button onClick={() => setCollapsed(true)} style={{
            background: "transparent", border: "none",
            color: T.textLow, cursor: "pointer",
            padding: 6, display: "flex", alignItems: "center",
            borderRadius: 8, transition: "all 0.2s ease",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = T.bgBase; e.currentTarget.style.color = T.textHi; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.textLow; }}
          >
            <ChevronLeft size={16}/>
          </button>
        )}
        {collapsed && (
          <button onClick={() => setCollapsed(false)} style={{
            position: "absolute", left: 68, top: 28,
            background: T.bgCard, border: `1px solid ${T.border}`,
            borderRadius: 8, color: T.textLow, cursor: "pointer",
            padding: "4px", display: "flex", alignItems: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)", zIndex: 10,
            transition: "all 0.2s ease",
          }}
          onMouseEnter={e => e.currentTarget.style.color = T.textHi}
          onMouseLeave={e => e.currentTarget.style.color = T.textLow}
          >
            <ChevronRight size={14}/>
          </button>
        )}
      </div>

      {/* Nav */}
      <nav style={{ flex: 1, padding: "8px 16px", display: "flex", flexDirection: "column", overflowY: "auto", gap: 24 }}>
        {GROUPS.map(group => {
          const visibleItems = group.items.filter(canSeeItem);
          if (visibleItems.length === 0) return null;
          return (
            <div key={group.label}>
              {!collapsed && (
                <div style={{
                  padding: "0 12px 10px",
                  fontSize: 11, fontWeight: 700, letterSpacing: "0.08em",
                  color: T.textLow, textTransform: "uppercase",
                  userSelect: "none",
                }}>
                  {group.label}
                </div>
              )}

              <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                {visibleItems.map(({ to, icon: Icon, label }) => (
                  <NavLink
                    key={to}
                    to={to}
                    end={to === "/"}
                    style={({ isActive }) => ({
                      display: "flex", alignItems: "center", gap: 12,
                      padding: collapsed ? "12px 0" : "10px 14px",
                      borderRadius: T.radiusSm,
                      textDecoration: "none",
                      justifyContent: collapsed ? "center" : "flex-start",
                      background: isActive ? T.accentDim : "transparent",
                      color: isActive ? T.accent : T.textMid,
                      fontFamily: T.fontBody, fontSize: 14, fontWeight: isActive ? 600 : 500,
                      transition: T.transitionFast,
                      position: "relative",
                    })}
                    onMouseEnter={e => { if (!e.currentTarget.className.includes("active")) { e.currentTarget.style.background = T.bgBase; e.currentTarget.style.color = T.textHi; } }}
                    onMouseLeave={e => { if (!e.currentTarget.className.includes("active")) { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = T.textMid; } }}
                  >
                    {({ isActive }) => (
                       <>
                         <Icon size={18} strokeWidth={isActive ? 2.5 : 2} style={{ flexShrink: 0 }}/>
                         {!collapsed && <span>{label}</span>}
                         {isActive && collapsed && (
                           <div style={{
                             position: "absolute", left: -4, top: "50%", transform: "translateY(-50%)",
                             width: 4, height: 20, borderRadius: "0 4px 4px 0", background: T.accent
                           }}/>
                         )}
                       </>
                    )}
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
    </div>
  );
}

